// public-reader/components/TranslateBanner.tsx — auto-detect translation
// banner shown when the visitor's preferred language differs from the
// book's source language.
//
// One tap → ReaderRoute re-fetches the book in the visitor's language
// and chapter bodies start translating lazily. Dismiss → banner closes
// for this slug for the rest of the session (no localStorage; just
// component state in the parent, so a refresh shows it again).

import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { findSupportedLanguage } from "../../i18n/supportedLanguages";

export interface TranslateBannerProps {
  /** The book's source language (e.g. "en"). */
  originalLang: string;
  /** The visitor's preferred language (e.g. "fr"). */
  userLang: string;
  /** True while the post-tap re-fetch is in flight. */
  busy: boolean;
  /** Tap handler — kick off the language switch. */
  onAccept: () => void;
  /** Dismiss handler — hide for the rest of the session. */
  onDismiss: () => void;
  /** Theme tokens from ReaderRoute. */
  accent: string;
  accentMuted: string;
  ink: string;
  inkMuted: string;
  paper: string;
  paperEdge: string;
}

export default function TranslateBanner(
  props: TranslateBannerProps,
): JSX.Element {
  const { t } = useTranslation(["public-reader"]);
  const userEntry = findSupportedLanguage(props.userLang);
  const origEntry = findSupportedLanguage(props.originalLang);
  // Native names read better in the banner — a French reader sees
  // "Lire en Français ?" rather than "Lire en French ?". Fall back to
  // the code if we can't find an entry (defensive against stale
  // localStorage values).
  const userName = userEntry?.nativeName ?? props.userLang;
  const origName = origEntry?.nativeName ?? props.originalLang;

  const banner: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 20px",
    background: props.paper,
    borderBottom: `1px solid ${props.paperEdge}`,
    color: props.ink,
    fontSize: "0.9rem",
    flexWrap: "wrap",
  };
  const label: CSSProperties = {
    flex: 1,
    minWidth: 200,
    fontStyle: "italic",
    color: props.ink,
  };
  const acceptBtn: CSSProperties = {
    background: "transparent",
    color: props.accent,
    border: `1px solid ${props.accent}`,
    padding: "6px 14px",
    fontSize: "0.85rem",
    fontFamily: "inherit",
    cursor: props.busy ? "default" : "pointer",
    borderRadius: 2,
    opacity: props.busy ? 0.7 : 1,
  };
  const dismissBtn: CSSProperties = {
    background: "transparent",
    color: props.inkMuted,
    border: `1px solid ${props.paperEdge}`,
    padding: "6px 12px",
    fontSize: "0.8rem",
    fontFamily: "inherit",
    cursor: props.busy ? "default" : "pointer",
    borderRadius: 2,
  };

  return (
    <div style={banner} role="region" aria-live="polite">
      <span style={label}>
        {t("public-reader:translateBanner.label", { language: userName })}
      </span>
      <button
        type="button"
        style={acceptBtn}
        onClick={props.onAccept}
        disabled={props.busy}
      >
        {props.busy
          ? t("public-reader:translateBanner.translating")
          : t("public-reader:translateBanner.cta")}
      </button>
      <button
        type="button"
        style={dismissBtn}
        onClick={props.onDismiss}
        disabled={props.busy}
      >
        {t("public-reader:translateBanner.dismiss", {
          originalLanguage: origName,
        })}
      </button>
    </div>
  );
}
